import { useQuery } from "@tanstack/react-query";

import { SearchFiltersAtom } from "@/src/state/dealer/dealerActiveAuctionSearchFiltersAtom";

import { VehicleType } from "@/src/pages/dealer/components/VehicleCheckBoxGroup/VehicleCheckBoxGroup";

import { useGetServerTime } from "@/src/hooks/useGetServerTime";
import {
  DealerQueryKeys,
  getActiveAuctions
} from "@/src/queries/dealer/dealerFunctions";
import {
  AuctionState,
  DealerAuctionDTO,
  DeliveryMethod
} from "@/src/types/types";
import { NETTIX_ID_TO_CARAVAN_TYPE } from "@/src/utils/caravanTypes";

import { parseISO } from "date-fns";

const filterVehiclesByType = (
  auctions: DealerAuctionDTO[],
  vehicleType: VehicleType[]
): DealerAuctionDTO[] => {
  const hasAllTypes =
    vehicleType.includes(VehicleType.CAR) &&
    vehicleType.includes(VehicleType.MOTO) &&
    vehicleType.includes(VehicleType.CARAVAN);

  if (hasAllTypes) {
    return auctions.filter(
      (auction) => auction.auctionItem.vehicle?.vehicleType !== undefined
    );
  }

  return auctions.filter(
    (auction) =>
      auction.auctionItem.vehicle?.vehicleType !== undefined &&
      vehicleType.includes(auction.auctionItem.vehicle.vehicleType)
  );
};

export const useGetMakes = (vehicleType: VehicleType[]) => {
  return useQuery({
    queryKey: [DealerQueryKeys.AUCTIONS, vehicleType],
    queryFn: () => getActiveAuctions(),
    select: (auctionItems) => {
      const activeAuctions = auctionItems.filter(
        (item: DealerAuctionDTO) => item.auctionState === AuctionState.STARTED
      );

      const filteredAuctions = filterVehiclesByType(
        activeAuctions,
        vehicleType
      );

      // Get unique makes for the filtered vehicle type
      const uniqueMakes = [
        ...new Set(
          filteredAuctions
            .map((auction) => auction.auctionItem.vehicle?.make)
            .filter((make): make is string => make !== undefined)
        )
      ];

      return uniqueMakes;
    }
  });
};

export const useGetBikeTypes = () => {
  return useQuery({
    queryKey: [DealerQueryKeys.AUCTIONS],
    queryFn: () => getActiveAuctions(),
    select: (auctionItems) => {
      // First filter for active auctions and only return MOTO vehicles
      const auctions = auctionItems.filter(
        (item: DealerAuctionDTO) => item.auctionState === AuctionState.STARTED
      );

      const motoAuctions = auctions.filter(
        (auction) =>
          auction.auctionItem.vehicle?.vehicleType === VehicleType.MOTO
      );

      const uniqueBikeTypes = [
        ...new Set(
          motoAuctions.map((auction) => auction.auctionItem.vehicle?.bikeType)
        )
      ];

      return uniqueBikeTypes;
    }
  });
};

export const useGetFuelTypes = (
  vehicleType: VehicleType[],
  searchFilters?: SearchFiltersAtom
) => {
  const { currentTime } = useGetServerTime();

  return useQuery({
    queryKey: [DealerQueryKeys.AUCTIONS],
    queryFn: () => getActiveAuctions(),
    select: (auctionItems) => {
      const currentDate = new Date(currentTime);

      // Same "currently visible" scope as useGetAllActiveAuctions: STARTED and
      // not yet past its endTime (auctions can remain STARTED briefly after
      // ending, before a cleanup job transitions them).
      const activeAuctions = auctionItems.filter(
        (item: DealerAuctionDTO) =>
          item.auctionState === AuctionState.STARTED &&
          item.endTime &&
          parseISO(item.endTime).getTime() > currentDate.getTime()
      );

      // Apply the rest of the current search filters (make, year, price,
      // mileage, gearType, distance) so options match what's actually
      // filterable, excluding fuelType itself so every option stays visible.
      const filteredAuctions = getFilteredItems(activeAuctions, {
        ...searchFilters,
        vehicleType,
        fuelType: undefined
      });

      const uniqueFuels = [
        ...new Set(
          filteredAuctions.map(
            (auction) => auction.auctionItem.vehicle?.fuelType
          )
        )
      ];

      return uniqueFuels.filter((fuel) => fuel !== undefined).sort();
    }
  });
};

export const useGetGearTypes = (vehicleType: VehicleType[]) => {
  return useQuery({
    queryKey: [DealerQueryKeys.AUCTIONS],
    queryFn: () => getActiveAuctions(),
    select: (auctionItems) => {
      const activeAuctions = auctionItems.filter(
        (item: DealerAuctionDTO) => item.auctionState === AuctionState.STARTED
      );

      const filteredAuctions = filterVehiclesByType(
        activeAuctions,
        vehicleType
      );

      const uniqueGears = [
        ...new Set(
          filteredAuctions.map(
            (auction) => auction.auctionItem.vehicle?.gearType
          )
        )
      ];
      // gearType might be missing in some cases. Remove empty values.
      return uniqueGears.filter((gearType) => gearType !== undefined);
    }
  });
};

export const getFilteredItems = (
  items: DealerAuctionDTO[],
  searchFilters: SearchFiltersAtom | undefined
) => {
  if (searchFilters?.vehicleType && searchFilters?.vehicleType?.length > 0) {
    items = items.filter((item) => {
      return searchFilters?.vehicleType?.find(
        (vehicleType) => item.auctionItem.vehicle?.vehicleType === vehicleType
      );
    });
  }
  if (searchFilters?.bikeType && searchFilters?.bikeType?.length > 0) {
    items = items.filter((item) => {
      return searchFilters?.bikeType?.find(
        (bikeType) => item.auctionItem.vehicle?.bikeType === bikeType
      );
    });
  }
  if (
    searchFilters?.caravanSubtype &&
    searchFilters?.caravanSubtype?.length > 0
  ) {
    items = items.filter((item) => {
      const vehicleTypeId = item.auctionItem.vehicle?.vehicleTypeId;
      if (!vehicleTypeId) return false;
      const caravanType = NETTIX_ID_TO_CARAVAN_TYPE[vehicleTypeId];
      return searchFilters?.caravanSubtype?.find(
        (subtype) => caravanType === subtype
      );
    });
  }
  if (searchFilters?.make && searchFilters?.make?.length > 0) {
    items = items.filter((item) => {
      return searchFilters?.make?.find(
        (make) => item.auctionItem.vehicle?.make === make
      );
    });
  }
  if (searchFilters?.modelYear?.start) {
    items = items.filter((item) => {
      return item.auctionItem.vehicle?.modelYear &&
        searchFilters?.modelYear?.start
        ? searchFilters?.modelYear?.start <= item.auctionItem.vehicle?.modelYear
        : false;
    });
  }
  if (searchFilters?.modelYear?.end) {
    items = items.filter((item) => {
      return item.auctionItem.vehicle?.modelYear &&
        searchFilters?.modelYear?.end
        ? searchFilters?.modelYear?.end >= item.auctionItem.vehicle?.modelYear
        : false;
    });
  }
  if (searchFilters?.targetPrice?.start) {
    items = items.filter((item) => {
      return item.auctionItem.auctionItemDetails?.targetPrice &&
        searchFilters?.targetPrice?.start
        ? searchFilters?.targetPrice?.start <=
            item.auctionItem.auctionItemDetails.targetPrice
        : false;
    });
  }
  if (searchFilters?.targetPrice?.end) {
    items = items.filter((item) => {
      return item.auctionItem.auctionItemDetails?.targetPrice &&
        searchFilters?.targetPrice?.end
        ? searchFilters?.targetPrice?.end >=
            item.auctionItem.auctionItemDetails?.targetPrice
        : false;
    });
  }
  if (searchFilters?.fuelType && searchFilters?.fuelType?.length > 0) {
    items = items.filter((item) => {
      return searchFilters?.fuelType?.find(
        (fuel) => item.auctionItem.vehicle?.fuelType === fuel
      );
    });
  }
  if (searchFilters?.distance && searchFilters?.distance > 0) {
    items = items.filter((item) => {
      const isWithinDistance =
        item.distanceBetween &&
        searchFilters?.distance &&
        item.distanceBetween <= searchFilters.distance;

      const isWillingToDeliver =
        searchFilters.sellerDelivers &&
        item.auctionItem.auctionItemDetails?.delivery?.method ===
          DeliveryMethod.DELIVER &&
        item.distanceBetween &&
        item.auctionItem.auctionItemDetails?.delivery?.range &&
        item.distanceBetween <=
          item.auctionItem.auctionItemDetails.delivery.range;

      return isWithinDistance || isWillingToDeliver;
    });
  }

  if (searchFilters?.mileage?.start) {
    items = items.filter((item) => {
      return item.auctionItem.auctionItemDetails?.mileage &&
        searchFilters?.mileage?.start
        ? searchFilters.mileage.start <=
            item.auctionItem.auctionItemDetails.mileage
        : false;
    });
  }
  if (searchFilters?.mileage?.end) {
    items = items.filter((item) => {
      return item.auctionItem.auctionItemDetails?.mileage &&
        searchFilters?.mileage?.end
        ? searchFilters.mileage.end >=
            item.auctionItem.auctionItemDetails.mileage
        : false;
    });
  }

  if (searchFilters?.gearType && searchFilters?.gearType?.length > 0) {
    items = items.filter((item) => {
      return searchFilters?.gearType?.find(
        (gearType) => item.auctionItem.vehicle?.gearType === gearType
      );
    });
  }
  return items;
};
